import React, { useEffect, useRef } from 'react';
import { ThemeProvider, CssBaseline, Container, Box } from '@mui/material';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import theme from './theme';
import Header from './components/Header';
import Footer from './components/Footer';
import Home from './pages/Home';
import AboutAyurveda from './pages/AboutAyurveda';
import AboutParitranam from './pages/AboutParitranam';
import Products from './pages/Products';
import Gallery from './pages/Gallery';
import Contact from './pages/Contact';
import { LanguageMiddleware } from './middleware/LanguageMiddleware';
import ScrollToTop from './components/ScrollToTop';
import FloatingLeaves from './components/FloatingLeaves';
import ConsultancyPopup from './pages/ConsultancyPopup.jsx';
import SocialSidebar from './components/SocialSidebar';
import sound from "./music/homesound.mp3"

function App() {
  const audioRef = useRef(null);

  useEffect(() => {
    // Create audio using the imported file
    const audio = new Audio(sound);
    audioRef.current = audio;
    
    // Configure audio
    audio.loop = true;
    audio.autoplay = true;
    audio.preload = 'auto';
    audio.volume = 0; // Start muted
    audio.currentTime = 0;

    // Helper to unmute
    const fadeToFull = () => {
      if (audioRef.current) {
        audioRef.current.volume = 1;
      }
    };

    // Interaction handler if autoplay is blocked
    const handleInteraction = () => {
      if (audioRef.current && audioRef.current.paused) {
        audioRef.current.play()
          .then(fadeToFull)
          .catch(e => console.error("Play failed even after interaction:", e));
      }
      // Remove listeners once we've tried to play
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };

    // Attempt autoplay
    audio.play()
      .then(() => {
        // Autoplay worked! Fade in volume.
        setTimeout(fadeToFull, 300);
      })
      .catch(() => {
        // Autoplay blocked. Wait for any user interaction.
        document.addEventListener('click', handleInteraction);
        document.addEventListener('keydown', handleInteraction);
        document.addEventListener('touchstart', handleInteraction);
      });

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current = null;
      }
      // Cleanup listeners
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('keydown', handleInteraction);
      document.removeEventListener('touchstart', handleInteraction);
    };
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter future={{ v7_relativeSplatPath: true }}>
        <LanguageMiddleware>
          <ScrollToTop />
          
          <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            <ConsultancyPopup/>
            <Header />
            <SocialSidebar/>
            <FloatingLeaves />
            <Box sx={{ height: { xs: 96, md: 64 } }} />
            <Box sx={{ flex: 1 }}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/home" element={<Home />} />
                <Route path="/about-ayurveda" element={<AboutAyurveda />} />
                <Route path="/about-paritranam" element={<AboutParitranam />} />
                <Route path="/products" element={<Products />} />
                <Route path="/gallery" element={<Gallery />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="*" element={<Navigate to="/home" replace />} />
              </Routes>
            </Box>
            <Footer />
          </Box>
        </LanguageMiddleware>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
